import React, { useEffect, useState, useContext, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Switch,
  FormControlLabel,
  Button,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PageBody from "../GeneralComponents/PageBody";
import PageBgBlock from "../GeneralComponents/PageBgBlock";
import ListEmpty from "../GeneralComponents/ListEmpty";
import GeneralContext from "../GeneralContext/GeneralContext";
import axios from "../../api/axios";
import dayjs from "dayjs";

const METRICS = [
  { key: "trafico_subida",               label: "Tráfico promedio de subida" },
  { key: "trafico_bajada",               label: "Tráfico promedio de bajada" },
  { key: "trafico_bajada_internacional", label: "Tráfico promedio de bajada internacional" },
  { key: "latencia",                     label: "Latencia" },
  { key: "perdida_paquete",              label: "Perdida de paquete" },
];

const SALMON = "#F4BBAA";
const GROUP_BG = "rgba(25, 118, 210, 0.10)";

function groupRows(rows, enabledKeys) {
  if (!enabledKeys.length) return [{ id: "__all__", partsMap: {}, rows }];
  const map = new Map();
  for (const r of rows) {
    const key = enabledKeys
      .map((k) => `${k}:${r?.[k] == null ? "N/A" : String(r[k])}`)
      .join("|");
    if (!map.has(key)) {
      const partsMap = {};
      enabledKeys.forEach((k) => (partsMap[k] = r?.[k] ?? null));
      map.set(key, { id: key, partsMap, rows: [] });
    }
    map.get(key).rows.push(r);
  }
  const groups = Array.from(map.values());
  groups.sort((a, b) => a.id.localeCompare(b.id, "es", { numeric: true }));
  return groups;
}

function buildGroupTitle(keys, partsMap) {
  const label = { operador: "Operador", zona: "Zona" };
  return keys
    .map((k) => `${label[k] ?? k}: ${partsMap[k] == null ? "N/A" : partsMap[k]}`)
    .join(" - ");
}

function tiempoStr(dias) {
  if (dias === 0) return "hoy";
  if (dias === 1) return "hace 1 día";
  return `hace ${dias} días`;
}

const COLS = [
  { key: "rbd",             header: "RBD" },
  { key: "zona",            header: "Zona" },
  { key: "operador",        header: "Operador" },
  { key: "convocatoria",    header: "Convocatoria" },
  { key: "nombre",          header: "Nombre" },
  { key: "_tiempo",         header: "Tiempo" },
  { key: "primer_dia_cero", header: "Desde" },
];

function DataTable({ rows, totalCount, page, rowsPerPage, onPageChange, onRowsPerPageChange, showPagination, sortConfig, onSort }) {
  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {COLS.map((c) => (
                <TableCell key={c.key} sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortConfig?.key === c.key}
                    direction={sortConfig?.key === c.key ? sortConfig.direction : "asc"}
                    onClick={() => onSort?.(c.key)}
                  >
                    {c.header}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLS.length}><ListEmpty /></TableCell>
              </TableRow>
            ) : (
              rows.map((r, idx) => (
                <TableRow key={`${r.rbd}-${idx}`} hover>
                  {COLS.map((c) => (
                    <TableCell key={c.key}>{r[c.key] ?? ""}</TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {showPagination && (
        <TablePagination
          component="div"
          count={totalCount ?? 0}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Rows per page:"
        />
      )}
    </>
  );
}

export default function RbdSinMovimiento() {
  const { setPageTitle } = useContext(GeneralContext);

  const today = dayjs().format("YYYY-MM-DD");
  const firstOfYear = dayjs().startOf("year").format("YYYY-MM-DD");

  const [fechaInicio, setFechaInicio] = useState(firstOfYear);
  const [fechaFin, setFechaFin] = useState(today);
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const [pendingFechaInicio, setPendingFechaInicio] = useState(firstOfYear);
  const [pendingFechaFin, setPendingFechaFin] = useState(today);

  const [grouping, setGrouping] = useState({ operador: false, zona: false });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
    setPage(0);
  };

  useEffect(() => {
    setPageTitle?.("RBD SIN MOVIMIENTO DE TRÁFICO");
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setFetchError(false);
      try {
        const resp = await axios.getRbdSinMovimiento({ fecha_inicio: fechaInicio, fecha_fin: fechaFin });
        setApiData(resp?.data ?? null);
      } catch {
        setApiData(null);
        setFetchError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [fechaInicio, fechaFin, refetchTrigger]);

  const dateErrors = (() => {
    const inicio = pendingFechaInicio;
    const fin = pendingFechaFin;
    let errorInicio = "";
    let errorFin = "";
    if (!inicio) {
      errorInicio = "Ingrese una fecha de inicio.";
    }
    if (!fin) {
      errorFin = "Ingrese una fecha de término.";
    }
    if (inicio && fin && dayjs(inicio).isAfter(dayjs(fin))) {
      errorInicio = "La fecha de inicio no puede ser posterior a la fecha de término.";
    }
    if (fin && dayjs(fin).isAfter(dayjs(today))) {
      errorFin = "La fecha de término no puede ser posterior a hoy.";
    }
    return { inicio: errorInicio, fin: errorFin };
  })();

  const hasDateError = !!(dateErrors.inicio || dateErrors.fin);

  const handleAplicar = () => {
    if (hasDateError) return;
    setFechaInicio(pendingFechaInicio);
    setFechaFin(pendingFechaFin);
    setRefetchTrigger((t) => t + 1);
    setPage(0);
  };

  const metricas = apiData?.metricas ?? {};

  const rawDatos = useMemo(() => {
    const list = apiData?.data ?? [];
    return list
      .filter((r) => r.estado_conectividad === "Activo" && r.estado_pago === "Facturar")
      .map((r) => ({
        ...r,
        zona: r.id_zona,
        operador: r.nombre_operador,
        convocatoria: r.concurso,
        nombre: r.nombre_ees,
        _dias: r.dias_con_ceros ?? 0,
        _tiempo: tiempoStr(r.dias_con_ceros ?? 0),
      }));
  }, [apiData]);

  const totalRbd = rawDatos.length;

  const filteredDatos = rawDatos;

  const sortedDatos = useMemo(() => {
    if (!sortConfig.key) return filteredDatos;
    const { key, direction } = sortConfig;
    return [...filteredDatos].sort((a, b) => {
      const sortKey = key === "_tiempo" ? "_dias" : key;
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";
      if (av === bv) return 0;
      const cmp =
        typeof av === "number" && typeof bv === "number"
          ? av - bv
          : String(av).localeCompare(String(bv), "es", { numeric: true });
      return direction === "asc" ? cmp : -cmp;
    });
  }, [filteredDatos, sortConfig]);

  const enabledGroupKeys = useMemo(() => {
    const keys = [];
    if (grouping.operador) keys.push("operador");
    if (grouping.zona) keys.push("zona");
    return keys;
  }, [grouping]);

  const isGrouped = enabledGroupKeys.length > 0;

  const pagedDatos = useMemo(
    () => sortedDatos.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [sortedDatos, page, rowsPerPage]
  );

  const groups = useMemo(
    () => groupRows(sortedDatos, enabledGroupKeys),
    [sortedDatos, enabledGroupKeys]
  );

  const handleDownloadCSV = () => {
    const headers = COLS.map((c) => c.header);
    const keys = COLS.map((c) => c.key);
    const rows = sortedDatos.map((r) =>
      keys.map((k) => `"${String(r[k] ?? "").replace(/"/g, '""')}"`).join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rbd_sin_movimiento_${fechaInicio}_${fechaFin}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <PageBody>
      <Box width="100%">
        <Typography variant="h1" sx={{ textAlign: "center", p: "0.6rem" }}>
          RBD SIN MOVIMIENTO DE TRÁFICO
        </Typography>
        <Typography variant="body2" sx={{ textAlign: "center", mb: 3 }}>
          Todos los RBD que se nombran aquí están con{" "}
          <strong>Estado de servicio: Conectado</strong> y{" "}
          <strong>Estado de pago: Facturando</strong>
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "40vh" }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Cargando...</Typography>
          </Box>
        ) : fetchError ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "40vh" }}>
            <Typography color="error" sx={{ mb: 2 }}>
              Ocurrió un error al obtener los datos. Intenta nuevamente.
            </Typography>
            <Button variant="contained" onClick={() => setRefetchTrigger((t) => t + 1)}>
              Reintentar
            </Button>
          </Box>
        ) : (
          <>
            {/* Metrics summary */}
            <Paper elevation={0} sx={{ mb: 3, border: "1px solid rgba(0,0,0,0.15)" }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, background: "#e0e0e0", width: 300 }}>Métricas</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, background: "#e0e0e0", width: 300 }}>Medida de datos</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, background: SALMON }}>Cantidad de RBD</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {METRICS.map((m, idx) => (
                      <TableRow key={m.key}>
                        <TableCell sx={{ fontSize: "0.85rem" }}>{m.label}</TableCell>
                        <TableCell align="center">{metricas[m.key] ?? 0}</TableCell>
                        {idx === 0 && (
                          <TableCell
                            align="center"
                            rowSpan={METRICS.length}
                            sx={{ background: SALMON, fontWeight: 900, fontSize: "2.5rem", verticalAlign: "middle" }}
                          >
                            {totalRbd.toLocaleString()}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {/* Filters */}
            <Box sx={{ display: "flex", alignItems: "stretch", mb: 3, border: "1px solid rgba(0,0,0,0.15)", borderRadius: 1, background: "#fff" }}>
              {/* Date pickers + Aplicar */}
              <Box sx={{ flex: "0 0 600px", p: 2.5, display: "flex", flexDirection: "column", borderRight: "1px solid rgba(0,0,0,0.15)" }}>
                <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" sx={{ mb: 0.75, fontWeight: 500 }}>Desde:</Typography>
                    <TextField
                      type="date"
                      size="small"
                      fullWidth
                      value={pendingFechaInicio}
                      onChange={(e) => setPendingFechaInicio(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ max: today }}
                      error={!!dateErrors.inicio}
                      helperText={dateErrors.inicio}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" sx={{ mb: 0.75, fontWeight: 500 }}>Hasta:</Typography>
                    <TextField
                      type="date"
                      size="small"
                      fullWidth
                      value={pendingFechaFin}
                      onChange={(e) => setPendingFechaFin(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ max: today }}
                      error={!!dateErrors.fin}
                      helperText={dateErrors.fin}
                    />
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  onClick={handleAplicar}
                  disabled={hasDateError}
                  sx={{ background: "#C8D8F0", color: "#1565c0", boxShadow: "none", "&:hover": { background: "#b3c8e8", boxShadow: "none" } }}
                  fullWidth
                >
                  Aplicar
                </Button>
              </Box>

              {/* Agrupar por */}
              <Box sx={{ flex: 1, p: 2.5 }}>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>Agrupar por:</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={grouping.operador}
                      onChange={(e) => setGrouping((p) => ({ ...p, operador: e.target.checked }))}
                    />
                  }
                  label={<Typography variant="body1">Por Operador</Typography>}
                  sx={{ display: "flex", ml: 0, mb: 0.5 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={grouping.zona}
                      onChange={(e) => setGrouping((p) => ({ ...p, zona: e.target.checked }))}
                    />
                  }
                  label={<Typography variant="body1">Por Zona</Typography>}
                  sx={{ display: "flex", ml: 0 }}
                />
              </Box>
            </Box>

            {/* Data tables */}
            <PageBgBlock pad="0">
              {isGrouped && filteredDatos.length === 0 ? (
                <Paper elevation={0} sx={{ width: "100%", mb: 2 }}>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>Total RBD: 0</Typography>
                  </Box>
                  <DataTable rows={[]} showPagination={false} sortConfig={sortConfig} onSort={handleSort} />
                </Paper>
              ) : isGrouped ? (
                groups.map((g) => {
                  const title = buildGroupTitle(enabledGroupKeys, g.partsMap);
                  return (
                    <Accordion key={g.id} variant="outlined" sx={{ mb: 2 }} defaultExpanded={groups.length === 1}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ background: GROUP_BG, borderBottom: "1px solid rgba(0,0,0,0.12)" }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {title}&nbsp;&nbsp;
                          <Box component="span" sx={{ fontWeight: 400, color: "rgba(0,0,0,0.60)" }}>
                            Total RBD: {g.rows.length.toLocaleString()}
                          </Box>
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0 }}>
                        <DataTable rows={g.rows} showPagination={false} sortConfig={sortConfig} onSort={handleSort} />
                      </AccordionDetails>
                    </Accordion>
                  );
                })
              ) : (
                <Paper elevation={0} sx={{ width: "100%", mb: 2 }} >
                  <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Total RBD: {sortedDatos.length.toLocaleString()}
                    </Typography>
                  </Box>
                  <DataTable
                    rows={pagedDatos}
                    totalCount={sortedDatos.length}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
                    showPagination
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                </Paper>
              )}
            </PageBgBlock>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button variant="outlined" onClick={handleDownloadCSV}>
                Descargar detalle (.csv)
              </Button>
            </Box>
          </>
        )}
      </Box>
    </PageBody>
  );
}
